import {
    InvalidRoutePatternError,
    RouteConflictError,
    RouteNotFoundError,
} from './errors.js'
import { createAuthMiddleware } from './routeAuth.js'

// Validate route pattern
export function validateRoutePattern(pattern) {
    if (!pattern || typeof pattern !== 'string') {
        throw new InvalidRoutePatternError(
            pattern,
            'Pattern must be a non-empty string'
        )
    }

    if (!pattern.startsWith('/')) {
        throw new InvalidRoutePatternError(
            pattern,
            'Pattern must start with "/"'
        )
    }

    // Check for invalid characters or patterns
    if (pattern.includes('//')) {
        throw new InvalidRoutePatternError(
            pattern,
            'Pattern cannot contain consecutive slashes'
        )
    }

    // More validation rules can be added here
    return true
}

export function matchPattern(pattern, path) {
    // Convert pattern to regex, supporting wildcards and parameters
    // Examples:
    // "/files/*path" matches "/files/documents/readme.txt"
    // "/cameras/:deviceId/*path" matches "/cameras/laptop/stream"

    const paramNames = []

    let regexPattern = pattern
        .replace(/[.+^${}()|[\]\\]/g, '\\$&')
        .replace(/:[^/]+/g, (match) => {
            paramNames.push(match.slice(1)) // Remove the ':'
            return '([^/]+)'
        })
        .replace(/\*([^/]*)/g, (match, paramName) => {
            if (paramName) paramNames.push(paramName)
            return '(.*)'
        })
        .replace(/\//g, '\\/')

    const regex = new RegExp(`^${regexPattern}$`)
    const match = path.match(regex)

    if (!match) return null

    const params = {}
    paramNames.forEach((name, index) => {
        params[name] = match[index + 1]
    })

    return {
        params,
        remaining: paramNames.includes('path') ? params.path : '',
    }
}

// Compute per-segment specificity: literal=2, named-param=1, wildcard=0
export function routeSpecificity(pattern) {
    return pattern.split('/').filter(Boolean).map(seg => {
        if (seg.startsWith('*')) return 0
        if (seg.startsWith(':')) return 1
        return 2
    })
}

// Sort route entries by specificity (most specific first). Stable sort preserves insertion order for ties.
export function sortRouteEntries(entries) {
    return [...entries].sort((a, b) => {
        const specA = routeSpecificity(a[0])
        const specB = routeSpecificity(b[0])
        const maxLen = Math.max(specA.length, specB.length)
        for (let i = 0; i < maxLen; i++) {
            const sa = specA[i] ?? -1
            const sb = specB[i] ?? -1
            if (sb !== sa) return sb - sa
        }
        return 0
    })
}

export class Router {
    constructor(group) {
        this.group = group
        this.routeRegistry = {}
    }

    // Path pattern registration functions
    registerRoute(pattern, handler, metadata = {}, options = {}) {
        // Validate inputs
        validateRoutePattern(pattern)

        // Check for conflicts unless explicitly allowed
        if (this.routeRegistry[pattern] && !options.allowOverwrite) {
            const existingHandler = this.routeRegistry[pattern].instanceId
            throw new RouteConflictError(
                pattern,
                this.group.groupId,
                existingHandler,
                handler
            )
        }

        // Wrap handler with auth middleware if metadata.auth is present
        let wrappedHandler = handler
        if (metadata.auth) {
            wrappedHandler = createAuthMiddleware(handler, metadata.auth)
        }

        this.routeRegistry[pattern] = {
            handler: wrappedHandler,
            pattern,
            metadata: {
                ...metadata,
                registeredAt: Date.now(),
            },
        }

        console.log(
            `Registered route ${pattern} in group ${this.group.groupId}`
        )

        this._rebuildSortedEntries()

        // Notify all clients in group about updated routes
        this.publishRouteRegistry()
    }

    unregisterRoute(pattern) {
        if (this.routeRegistry && this.routeRegistry[pattern]) {
            delete this.routeRegistry[pattern]
            console.log(
                `Unregistered route ${pattern} from group ${this.group.groupId}`
            )
            this._rebuildSortedEntries()
            this.publishRouteRegistry()
        }
    }

    getRouteRegistry() {
        return this.routeRegistry || {}
    }

    _rebuildSortedEntries() {
        this._sortedEntries = sortRouteEntries(Object.entries(this.routeRegistry))
    }

    // Route matching function - supports wildcards and parameters
    // Routes are matched by specificity (literal > param > wildcard), not registration order
    matchRoute(requestPath) {
        const entries = this._sortedEntries || Object.entries(this.routeRegistry || {})

        for (const [pattern, routeInfo] of entries) {
            const match = matchPattern(pattern, requestPath)
            if (match) {
                return {
                    ...routeInfo,
                    pathParams: match.params,
                    remainingPath: match.remaining,
                }
            }
        }

        // Provide helpful error with available routes
        const availableRoutes = Object.keys(this.routeRegistry || {})
        throw new RouteNotFoundError(
            requestPath,
            this.group.groupId,
            availableRoutes
        )
    }

    publishRouteRegistry() {
        // Route registry is available via acequia.diagnoseRoutes() — no need to log on every registration
    }
}
